import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { AppFeature } from '@app/core/models/config.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { BrandingService } from '@app/core/services/branding.service';
import { MicrophonePermissionService } from '@app/core/services/microphone-permission.service';
import { AudioPlayerComponent } from '@app/shared/components/audio-player/audio-player.component';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import { Track } from '@app/shared/models/track.model';
import { VoiceMessageRecorder } from '@app/shared/models/voice-message.recorder.model';
import { SecondsToMinutesPipe } from '@app/shared/pipes/seconds-to-minutes.pipe';
import { AudioUtil } from '@app/shared/utils/audio.util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

import {
  AudioType,
  GreetingAudioList,
  MohAudioList,
  MohLanguages,
  MohSettings,
  VoiceOptions,
} from '../../models/settings.models';
import { SettingsPage } from '../../models/settings-page';
import { MusicOnHoldService } from '../../services/music-on-hold.service';
import { SettingsService } from '../../services/settings.service';
import { UploadMusicDialogComponent } from '../upload-music-dialog/upload-music-dialog.component';

@UntilDestroy()
@Component({
  selector: 'app-music-on-hold',
  templateUrl: './music-on-hold.component.html',
  styleUrls: ['./music-on-hold.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    TranslateModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatTooltipModule,
    DragDropModule,
    FormsModule,
    SecondsToMinutesPipe,
    MatRadioModule,
    MatSelectModule,
    AudioPlayerComponent,
    MatSliderModule,
    BrandingColorDirective,
  ],
})
export class MusicOnHoldComponent extends SettingsPage implements OnInit, AfterViewInit, OnDestroy {
  @Output() toggleEvent = new EventEmitter<boolean>();
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  @ViewChild(AudioPlayerComponent) player: AudioPlayerComponent;
  @ViewChild('container') container: ElementRef<HTMLElement>;
  @ViewChild('greyMask') greyMask?: ElementRef<HTMLDivElement>;

  protected mohSettings: MohSettings = {
    enableMoh: false,
    randomizeMoh: false,
    greetingType: 'TTS',
    greetingMessage: '',
    greetingLanguage: '',
    greetingVoice: '',
    greetingIndex: 0,
  };
  protected readonly AudioType = AudioType;
  protected enablePlayIntroductoryGreeting = false;
  protected mohFiles: MohAudioList[] = [];
  protected greetingFiles: GreetingAudioList[];
  protected voiceOptions: VoiceOptions[];
  protected languages: MohLanguages[] = [];
  protected greetingText: string;
  protected uploadGreetingFileName: string = '';
  private files: File[] = [];
  protected showUploadOptions: boolean = false;
  protected greetingChanged: boolean = false;

  protected inputSound: AudioUtil = new Audio() as AudioUtil;
  protected recordedFile: File | null = null;
  protected showUploadAudioPlayer: boolean = false;
  protected showGreetingAudioPlayer: boolean = false;
  protected track: Track = { title: '', link: '' };
  protected recordingFound: boolean = false;
  private remoteAudioElement = new Audio() as AudioUtil;
  protected playingIndex: number | null = null;
  protected readonly Number = Number;
  protected recordingIntervalId: NodeJS.Timeout;
  protected recordingSeconds: number = 0;
  protected playingAudioId: string | null;
  private playingAudio = new BehaviorSubject<AudioUtil | undefined>(undefined);
  protected editingDisabled = true;
  protected readonly BrandingField = BrandingField;
  protected defaultBrandingSettings = defaultBrandingSettings;

  protected voiceMessageRecorder: VoiceMessageRecorder;

  private _objectURL?: string;
  private get objectURL(): string | undefined {
    return this._objectURL;
  }
  private set objectURL(url: string) {
    this.tryReleaseObjectURL();
    this._objectURL = url;
  }

  constructor(
    private musicOnHoldService: MusicOnHoldService,
    private appConfigService: AppConfigService,
    private settingsService: SettingsService,
    private dialog: MatDialog,
    private appTranslateService: AppTranslateService,
    private microphonePermissionService: MicrophonePermissionService,
    protected brandingService: BrandingService
  ) {
    super();

    this.voiceMessageRecorder = new VoiceMessageRecorder(this.microphonePermissionService);
    this.voiceMessageRecorder.setSampleRate(8000);

    this.voiceMessageRecorder.state$.pipe(untilDestroyed(this)).subscribe(async (state) => {
      if (state === 'finished') {
        this.recordedFile = await this.voiceMessageRecorder.getRecordingAsWavFile('RecordedGreeting.wav');

        this.objectURL = URL.createObjectURL(this.recordedFile);
        this.inputSound.src = this.objectURL;
        this.track = {
          link: this.inputSound.src,
          title: 'RecordedGreeting.wav',
          duration: this.voiceMessageRecorder.duration,
        };
        this.inputSound.load();
        this.showGreetingAudioPlayer = true;
        this.recordingSeconds = 0;
      }
    });
    this.voiceMessageRecorder.elapsedTime$.pipe(untilDestroyed(this)).subscribe((time) => {
      this.recordingSeconds = time;
    });
  }

  async ngOnInit() {
    try {
      this.editingDisabled = !this.appConfigService.features[AppFeature.ModifyMusicOnHold];

      const [languages, settings] = await Promise.all([
        this.appConfigService.getTextToSpeechLanguages(),
        this.appConfigService.getLocalMohSettings(),
        this.getAudioFiles(),
      ]);

      this.languages = languages.languages;
      this.mohSettings = settings;

      this.setupMohSettings();

      //Upload and Recording - check if greetingFiles contains a file
      //TTS - check if the message is empty then set enablePlayIntroductoryGreeting to false
      if (
        (this.mohSettings.greetingType === 'TTS' && this.mohSettings.greetingMessage.length > 0) ||
        (this.mohSettings.greetingType !== 'TTS' && this.greetingFiles.length > 0)
      ) {
        this.enablePlayIntroductoryGreeting = true;
      }
    } catch (error) {
      console.error('music-on-hold.component --> ngOnInit:', error.message);
    }
  }

  ngAfterViewInit(): void {
    if (this.editingDisabled) {
      // pass scroll events from grey mask to main container
      this.greyMask?.nativeElement.addEventListener('wheel', (event: WheelEvent) => {
        this.container.nativeElement.scrollBy(event.deltaX, event.deltaY);
      });

      // block mouse and keyboard events
      this.container.nativeElement.addEventListener('click', this.stopAndPreventDefault, true);
      this.container.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Escape') {
          this.stopAndPreventDefault(event);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.voiceMessageRecorder.cancelRecording();
    this.tryReleaseObjectURL();

    this.inputSound.pause();
    this.inputSound.src = '';
    this.remoteAudioElement.pause();
    this.remoteAudioElement.src = '';
  }

  private stopAndPreventDefault(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  private async setupMohSettings() {
    //If TTS was set, load the voices based on language set
    switch (this.mohSettings.greetingType) {
      case 'TTS': {
        if (this.mohSettings.greetingLanguage) {
          await this.getTextToSpeechVoices(this.mohSettings.greetingLanguage);
        }
        break;
      }
      case 'Recording': {
        //Setup the audio file
        const greeting = this.greetingFiles.find((c) => c.index === this.mohSettings.greetingIndex);
        if (greeting) {
          this.inputSound.src = greeting.mediaUrl;
          this.recordingSeconds = Number(greeting.duration);
          this.track = { title: '', link: this.inputSound.src as string, duration: this.recordingSeconds };
          this.recordingFound = true;
          this.recordedFile = new File([greeting.mediaUrl], 'RecordedGreeting.wav', { type: 'audio/wav' });
          this.inputSound.load();
        }
        this.showGreetingAudioPlayer = this.inputSound.src === '' ? false : true;
        break;
      }
      case 'Upload': {
        //Setup the audio file
        const greeting = this.greetingFiles.find((c) => c.index === this.mohSettings.greetingIndex);
        if (greeting) {
          this.inputSound.src = greeting.mediaUrl;
          this.showUploadAudioPlayer = true;
          this.track = { title: '', link: this.inputSound.src as string };
        }
        break;
      }
    }

    return true;
  }

  private async getAudioFiles() {
    try {
      //Split out the greeting and moh files (there will only be one greeting file)
      const allFiles = await this.appConfigService.getAudioFiles();
      this.mohFiles = allFiles.filter((c) => c.index > 0);
      this.greetingFiles = allFiles.filter((c) => c.index === 0);
      this.mohFiles.sort((a, b) => a.index - b.index);
      this.mohFiles.forEach((file) => {
        //Include values needed for better handling of the data
        file.changed = false;
        file.edit = false;
      });
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  private async getTextToSpeechVoices(language: string) {
    this.voiceOptions = await this.appConfigService.getTextToSpeechVoices(language);
  }

  protected toggleMusicOnHoldSettings() {
    this.mohSettings.enableMoh = !this.mohSettings.enableMoh;
    this.disabled = false;
  }

  protected toggleRandomizeMusicOnHold() {
    this.mohSettings.randomizeMoh = !this.mohSettings.randomizeMoh;
    this.disabled = false;
  }

  private async updateMusicOnHoldSettings() {
    await this.appConfigService.setLocalMohSettings(this.mohSettings);
  }

  protected editDescription(data) {
    data.descriptionEdit = data.description;
    data.edit = true;
  }

  protected async saveDescriptionChange(data) {
    data.description = data.descriptionEdit;
    await this.appConfigService.updateAudioFiles(data.index, data);
    data.edit = false;
  }

  protected cancelDescriptionChange(data) {
    data.descriptionEdit = data.description;
    data.edit = false;
  }

  protected async onLanguageChanged(option: string) {
    this.mohSettings.greetingLanguage = option;
    await this.getTextToSpeechVoices(option);
    this.greetingChanged = true;
  }

  protected onVoiceChanged(option: string) {
    this.mohSettings.greetingVoice = option;
    this.disabled = false;
    this.greetingChanged = true;
  }

  async save() {
    if (this.editingDisabled) {
      return;
    }
    if (this.greetingChanged) {
      //Use index=0 for greeting files
      const newIndex = '0';
      if (this.enablePlayIntroductoryGreeting) {
        switch (this.mohSettings.greetingType) {
          case 'TTS': {
            //get the current highest index used and validate the index to make sure it's unique
            const ttsSettings = {
              description: this.appTranslateService.instant('MUSIC_ON_HOLD_PREFERENCES.TEXT_TO_SPEECH_GREETING'),
              voice_id: this.mohSettings.greetingVoice,
              voice_language: this.mohSettings.greetingLanguage,
              tts_script: this.mohSettings.greetingMessage,
              source_type: 'tts',
            };
            await this.appConfigService.addAudioFile(newIndex, AudioType.moh, ttsSettings);
            this.mohSettings.greetingIndex = Number(newIndex);
            break;
          }
          case 'Upload': {
            const fileType = this.files[0].type === 'audio/mpeg' ? 'audio/mp3' : this.files[0].type;
            const data = {
              description: this.uploadGreetingFileName,
              name: this.uploadGreetingFileName,
              format: fileType,
            };
            await this.appConfigService.uploadAudioFile(newIndex, data, this.files[0], AudioType.moh);
            this.mohSettings.greetingIndex = Number(newIndex);
            this.mohSettings.greetingMessage = '';
            this.mohSettings.greetingLanguage = '';
            this.mohSettings.greetingVoice = '';
            break;
          }
          case 'Recording': {
            if (this.recordedFile !== null) {
              const data = { description: 'RecordedGreeting', name: 'RecordedGreeting.wav' };
              await this.appConfigService.uploadAudioFile(newIndex, data, this.recordedFile, AudioType.moh);
            }
            this.mohSettings.greetingIndex = Number(newIndex);
            this.mohSettings.greetingMessage = '';
            this.mohSettings.greetingLanguage = '';
            this.mohSettings.greetingVoice = '';
            break;
          }
        }
      } else {
        await this.appConfigService.deleteAudioFile(Number(newIndex), AudioType.moh);
        this.mohSettings.greetingIndex = Number(newIndex);
        this.mohSettings.greetingMessage = '';
        this.mohSettings.greetingLanguage = '';
        this.mohSettings.greetingVoice = '';
      }
    }
    this.updateMusicOnHoldSettings();
    const newIndexOrder = this.mohFiles.map(({ index }) => index);

    //After all changes have been saved, reorder the files
    const orderChanged = this.mohFiles.some((file) => file.changed);
    if (orderChanged) {
      this.reorderMohFiles(newIndexOrder);
    }
  }

  private async reorderMohFiles(newIndexOrder) {
    try {
      await this.appConfigService.reorderAudioFiles(newIndexOrder.toString());
    } catch (error) {
      console.log(error);
    }
  }

  //Pass in the current index we want to use, check if it's valid and if not then increment it
  private generateIndex(value) {
    if (this.validateIndex(value)) {
      return value;
    } else {
      this.generateIndex(value + 1);
    }
  }

  private validateIndex(value) {
    const duplicateValues = this.mohFiles.filter((c) => c.index === value);
    return duplicateValues.length > 0 ? false : true;
  }

  protected togglePlayIntroGreeting() {
    this.enablePlayIntroductoryGreeting = !this.enablePlayIntroductoryGreeting;
    if (this.enablePlayIntroductoryGreeting) {
      if (
        this.mohSettings.greetingType === 'TTS' &&
        (this.mohSettings.greetingLanguage === null || this.mohSettings.greetingLanguage === '')
      ) {
        this.setDefaultLanguage();
      }
    } else {
      this.mohSettings.greetingIndex = 0;
      this.mohSettings.greetingMessage = '';
      this.mohSettings.greetingLanguage = '';
      this.mohSettings.greetingVoice = '';
      this.mohSettings.greetingType = 'TTS';
      this.disabled = false;
    }
    this.greetingChanged = true;
  }

  protected openFileUploadDialog() {
    const highestIndex =
      this.mohFiles
        .map((file) => file.index)
        .sort()
        .reverse()[0] ?? 0;
    const newIndex = this.generateIndex(highestIndex + 1);
    this.toggleEvent.emit(false);

    const dialogReference = this.dialog.open(UploadMusicDialogComponent, {
      data: {
        Name: this.appTranslateService.instant('MUSIC_ON_HOLD_PREFERENCES.TEST_FILE_NAME'),
        Id: newIndex,
      },
      hasBackdrop: true,
      panelClass: ['upload-music-dialog'],
      autoFocus: false,
    });

    dialogReference
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.getAudioFiles();
      });
  }

  protected onGreetingChange() {
    this.disabled = false;
    this.greetingChanged = true;
    if (this.mohSettings.greetingType === 'TTS') {
      if (this.mohSettings.greetingLanguage === null || this.mohSettings.greetingLanguage === '') {
        this.setDefaultLanguage();
      } else {
        this.onLanguageChanged(this.mohSettings.greetingLanguage);
      }
    }
  }

  private async setDefaultLanguage() {
    //Default to the browser language or english
    this.mohSettings.greetingLanguage = this.languages.some((item) => item.id === window.navigator.language)
      ? window.navigator.language
      : 'en-US';
    await this.getTextToSpeechVoices(this.mohSettings.greetingLanguage);
    //Default to the first voice in the list
    this.mohSettings.greetingVoice = this.voiceOptions[0].id;
  }

  protected selectGreetingFile() {
    this.fileInput.nativeElement.click();
  }

  protected addFile(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      this.files = Array.from(target.files);
      this.uploadGreetingFileName = this.files.map((file) => file.name).join('; ');
    }

    target.value = '';

    //Enable the save button
    this.greetingChanged = true;
    this.disabled = false;
  }

  protected playSelectedAudioFile(url: string, id: number) {
    this.remoteAudioElement.src = url;
    this.remoteAudioElement.load();
    this.remoteAudioElement.addEventListener('loadedmetadata', () => {
      this.playingIndex = id;
      this.settingsService.playAudio(this.remoteAudioElement).catch((error) => {
        console.error('Failed to play media!', error);
        this.playingIndex = null;
      });
    });

    this.remoteAudioElement.addEventListener('ended', () => {
      this.playingIndex = null;
    });
  }

  protected pauseSelectedAudioFile() {
    this.playingIndex = null;
    this.remoteAudioElement.pause();
  }

  protected async downloadAudioFile(id: number) {
    await this.musicOnHoldService.downloadAudioFile(id, AudioType.moh);
  }

  protected async deleteAudioFile(id: number) {
    try {
      await this.appConfigService.deleteAudioFile(id, AudioType.moh);
      //On success, reload the audio files
      this.getAudioFiles();
    } catch (error) {
      // Handle any error that occurs during the delete operation
      console.error('Error occurred while deleting audio file:', error);
    }
  }

  //Recording
  protected async startInputSound() {
    this.voiceMessageRecorder.startRecording(10);
  }

  protected stopInputSound() {
    this.inputSound.pause();
    this.voiceMessageRecorder.stopRecording();
    this.greetingChanged = true;
    this.disabled = false;
  }

  protected deleteRecording(id: number) {
    //Make sure recording has stopped
    if (this.voiceMessageRecorder.isRecording) {
      this.voiceMessageRecorder.cancelRecording();
    }

    //Reset the audio controls src so no file is loaded
    this.inputSound.src = '';
    this.tryReleaseObjectURL();

    //Reset the audio stream and file so it's not accidentally saved
    this.recordedFile = null;
    this.track.link = this.inputSound.src;
    this.showGreetingAudioPlayer = false;
    this.recordingFound = false;
    this.recordingSeconds = 0;
    this.deleteAudioFile(id);
  }

  protected async drop(evt: CdkDragDrop<MohAudioList[]>) {
    if (evt.previousContainer === evt.container) {
      //Reorders the array to match the display order
      await moveItemInArray(evt.container.data, evt.previousIndex, evt.currentIndex);

      //Use the changed flag so only the updated rows are saved
      for (let i = 0; i < this.mohFiles.length; i++) {
        if (i === evt.currentIndex || i === evt.previousIndex) {
          this.mohFiles[i].changed = true;
        }
      }
    } else {
      transferArrayItem(evt.previousContainer.data, evt.container.data, evt.previousIndex, evt.currentIndex);
    }

    //Enable the save button
    this.disabled = false;
  }

  protected async onRecordedFilePlayClicked() {
    if (this.track && this.playingAudioId != 'recorded') {
      const audioUtil: AudioUtil = new Audio(this.track.link) as AudioUtil;
      await this.playAudio(audioUtil);
      this.recordingSeconds = 0;
      this.recordingIntervalId = setInterval(() => this.recordingSeconds++, 1000);
      this.playingAudioId = 'recorded';
    } else {
      this.stopAudio();
    }
  }

  private async playAudio(audio: AudioUtil) {
    audio.addEventListener('play', () => {
      this.setPlayingAudio(audio);
    });
    audio.addEventListener('ended', () => {
      this.stopAudio();
    });
    await this.settingsService.playAudio(audio);
  }

  private stopAudio() {
    this.playingAudioId = null;
    this.recordingSeconds = 0;
    clearInterval(this.recordingIntervalId);
    this.setPlayingAudio();
  }

  private setPlayingAudio(audio?: AudioUtil) {
    const currentAudio = this.playingAudio.getValue();
    currentAudio?.pause();
    this.playingAudio.next(audio);
  }

  private tryReleaseObjectURL() {
    if (this._objectURL) {
      URL.revokeObjectURL(this._objectURL);
      this._objectURL = undefined;
    }
  }
}
